/**
 * O1 — the onboarding CHAPTER MAP: four named chapters, the doorstep that sits
 * before them, and the per-athlete arithmetic behind the progress treatment.
 *
 * Same posture as `onboardingCtaCopy.ts` (B45), `onboardingLite.ts` (B76C) and
 * `onboardingAgeCopy.ts` (REL-29): no React, no router, no CSS in the graph, so
 * the map, the strings and the counting rule are readable and pinnable on their
 * own — and `OnboardingShell`, `routes/onboarding.tsx` and the coaches reveal
 * can all read the SAME chapter without a component in between.
 *
 * ── WHERE THIS COMES FROM ─────────────────────────────────────────────────
 * The founder-ratified CD handoff `design_handoff_onboarding_chapters`
 * (2026-08-30): its "chapter map" table, its "Progress treatment" section, and
 * the `Onboarding Simulation.dc.html` reference implementation, which is the
 * tie-breaker wherever prose left a choice open. Every string in this file is
 * VERBATIM ratified copy from that bundle. Nothing here is authored.
 *
 * ── THE RULE THIS FILE EXISTS TO KEEP ─────────────────────────────────────
 * "Fill denominator is per-athlete: computed by the route from the screens
 * ACTUALLY queued (same source of truth as the B45 pending-beats flags), so
 * conditional beats never make the bar lie."
 *
 * That is the whole reason the flow is DERIVED here rather than listed. Three
 * things move it per athlete: the journey (competitive journeys answer pace
 * with the VDOT race-time form and never see the level question), the
 * training-data beat (0 to 3 questions, whichever items context is missing),
 * and the 13-to-15 guardian beat. A hardcoded "Question 2 of 4" would be wrong
 * for most athletes, which is exactly the class of bug B45 was opened for.
 *
 * ── WHAT THIS FILE DOES NOT DECIDE ────────────────────────────────────────
 * CTA labels. B45 owns those, and a chapter name may never appear on a CTA.
 * Nothing here is imported by `onboardingCtaCopy.ts`, and no screen is added to
 * its union: the chapter map describes WHERE a screen sits, never what its
 * button says.
 */

import type { StepNumber } from '../hooks/onboardingReducer';

// ──────────────────────────────────────────────────────────────────────────
// The chapters
// ──────────────────────────────────────────────────────────────────────────

export type ChapterNumber = 1 | 2 | 3 | 4;

/** The denominator in the "Chapter N of 4" kicker. */
export const CHAPTER_TOTAL = 4;

export type OnboardingChapter = {
  n: ChapterNumber;
  /** The serif `<h1>` in the ambient panel. */
  name: string;
  /** The context line under it. */
  context: string;
  /** The ambient panel background — one colour per chapter, not per step. */
  ambientBg: string;
};

/**
 * The four chapters, in order. Names and context lines are ratified copy.
 *
 * Chapter 3 and 4 reuse the context lines that already shipped on steps 5 and
 * 7 (the handoff marks both "existing"), so only chapters 1 and 2 introduce new
 * sentences — both dash-free, per the standing house rule.
 */
export const ONBOARDING_CHAPTERS: readonly OnboardingChapter[] = [
  {
    n: 1,
    name: 'Getting to know you',
    context: 'A few basics so your coaches know who they are talking to.',
    ambientBg: '#E4DDD5',
  },
  {
    n: 2,
    name: 'Checking the basics',
    context: 'Two quick checks so your plan starts at the right effort.',
    ambientBg: '#DDE3EB',
  },
  {
    n: 3,
    name: 'Finding your pace',
    context: 'Honest answers help your coaches start from the right place.',
    ambientBg: '#DCE6E6',
  },
  {
    n: 4,
    name: 'Meeting your coaches',
    context: 'Your coaching team is ready to meet you.',
    ambientBg: '#EBE4DB',
  },
];

export function chapterByNumber(n: ChapterNumber): OnboardingChapter {
  return ONBOARDING_CHAPTERS[n - 1];
}

/**
 * The doorstep ambient. The age step and the account fork sit BEFORE chapter 1
 * and keep their exact current chrome, so they wear chapter 1's sand without
 * claiming to be chapter 1.
 */
export const DOORSTEP_AMBIENT = '#E4DDD5';

// ──────────────────────────────────────────────────────────────────────────
// The screens, and which chapter owns each
// ──────────────────────────────────────────────────────────────────────────

/**
 * Every screen the athlete wizard can show, doorstep included.
 *
 * The three `data_beat_*` ids are POSITIONS in the training-data beat, not
 * identities: the beat asks whichever of its three items context is missing, in
 * its own fixed gate order, and only the COUNT and the position matter to the
 * progress treatment. `CollectionBeat` keeps the identities.
 */
export type ChapterScreenId =
  | 'age'
  | 'fork'
  | 'name'
  | 'journey'
  | 'location'
  | 'parq'
  | 'body'
  | 'level'
  | 'pace'
  | 'focus'
  | 'data_beat_1'
  | 'data_beat_2'
  | 'data_beat_3'
  | 'guardian'
  | 'welcome';

/** 0 = doorstep (before chapter 1). */
export type ChapterOrDoorstep = 0 | ChapterNumber;

/**
 * The map itself. Mirrors `chOf` in the ratified simulation exactly.
 *
 * `pace` is the competitive or time-goal journey's pace ask: the existing VDOT
 * race-time form (wizard step 5). A health or social journey has no separate
 * pace screen (ruling 3, 2026-10-22, removed "Do you know your usual pace?"):
 * its chapter 3 is the level question, the focus question and the data beat.
 */
export const CHAPTER_OF: Record<ChapterScreenId, ChapterOrDoorstep> = {
  age: 0,
  fork: 0,
  name: 1,
  journey: 1,
  location: 1,
  parq: 2,
  body: 2,
  level: 3,
  pace: 3,
  focus: 3,
  data_beat_1: 3,
  data_beat_2: 3,
  data_beat_3: 3,
  guardian: 4,
  welcome: 4,
};

/** The beat's three slots, in gate order. */
export const DATA_BEAT_SCREENS: readonly ChapterScreenId[] = [
  'data_beat_1',
  'data_beat_2',
  'data_beat_3',
];

/**
 * Which chapter screen each NUMBERED wizard step is.
 *
 * Step 5 is the one that moves, and it is the only one: for a competitive or
 * time-goal journey it renders the VDOT race-time form, which IS that
 * athlete's pace question, and for a health or social journey it renders the
 * level question. Same predicate `Step5Pace` itself branches on.
 *
 * The name screen is absent because it has no step number, by design —
 * numbering it would have renumbered every step after it, and the step number
 * is what a resumed localStorage blob and `?step=` are written against.
 */
export function wizardStepScreen(
  step: StepNumber,
  competitiveJourney: boolean,
): ChapterScreenId {
  switch (step) {
    case 1:
      return 'journey';
    case 2:
      return 'location';
    case 3:
      return 'parq';
    case 4:
      return 'body';
    case 5:
      return competitiveJourney ? 'pace' : 'level';
    case 6:
      return 'focus';
    case 7:
      return 'welcome';
    default: {
      const never: never = step;
      return never;
    }
  }
}

/**
 * Is this screen a numbered QUESTION in its chapter?
 *
 * Chapters 1 to 3 hold questions. The doorstep is unnumbered (frozen chrome,
 * no segments at all), and so is chapter 4: the guardian beat is a policy beat
 * with same-weight paths rather than a question, and the reveal is the payoff.
 * Same rule as the simulation's `isQuestion = ch >= 1 && ch <= 3`.
 */
export function isChapterQuestion(screen: ChapterScreenId): boolean {
  const ch = CHAPTER_OF[screen];
  return ch >= 1 && ch <= 3;
}

// ──────────────────────────────────────────────────────────────────────────
// The per-athlete flow
// ──────────────────────────────────────────────────────────────────────────

/**
 * The facts that move the flow, all of them already derived by the route for
 * other reasons.
 */
export type ChapterFlowFacts = {
  /** Is the new display-name screen queued as chapter 1's opener? */
  nameStepQueued: boolean;
  /**
   * Does this journey answer pace with the VDOT race-time form (wizard step 5)?
   * `COMPETITIVE_JOURNEYS` in `fixtures/onboarding` is the same predicate the
   * step itself branches on.
   */
  competitiveJourney: boolean;
  /**
   * How many questions the training-data beat will ask (0 to 3) — the length of
   * the MISSING list, i.e. the same fact behind B45's `dataBeatPending`.
   */
  dataBeatQuestions: number;
  /** UAT-B2 — is the 13-to-15 guardian beat still queued? */
  guardianBeatQueued: boolean;
};

/**
 * The screens THIS athlete will actually walk, in order, from chapter 1's
 * opener to the reveal. The doorstep is deliberately absent: it is not part of
 * any chapter, and including it would put unnumbered screens in a list whose
 * only job is counting numbered ones.
 */
export function deriveChapterFlow(facts: ChapterFlowFacts): ChapterScreenId[] {
  const flow: ChapterScreenId[] = [];

  // Chapter 1
  if (facts.nameStepQueued) flow.push('name');
  flow.push('journey', 'location');

  // Chapter 2
  flow.push('parq', 'body');

  // Chapter 3 — the branchy one.
  if (facts.competitiveJourney) {
    // The VDOT race-time form IS the pace question for these journeys, and
    // there is no separate level question.
    flow.push('pace');
  } else {
    flow.push('level');
  }
  flow.push('focus');
  const beats = Math.max(0, Math.min(DATA_BEAT_SCREENS.length, facts.dataBeatQuestions));
  flow.push(...DATA_BEAT_SCREENS.slice(0, beats));

  // Chapter 4
  if (facts.guardianBeatQueued) flow.push('guardian');
  flow.push('welcome');

  return flow;
}

/** The numbered questions this athlete walks in one chapter, in order. */
export function chapterQuestions(
  flow: readonly ChapterScreenId[],
  chapter: ChapterNumber,
): ChapterScreenId[] {
  return flow.filter((s) => CHAPTER_OF[s] === chapter && isChapterQuestion(s));
}

// ──────────────────────────────────────────────────────────────────────────
// What the shell needs to render
// ──────────────────────────────────────────────────────────────────────────

/**
 * One screen's place in the chaptered flow — everything the shell needs for the
 * kicker, the segments, the count line and the aria-live sentence.
 */
export type ChapterProgress = {
  n: ChapterNumber;
  /** 1-based position among this chapter's questions, or null when unnumbered. */
  questionIndex: number | null;
  /** How many questions this chapter holds FOR THIS ATHLETE. */
  questionCount: number;
  /**
   * 0 to 1 fill for the current segment on an UNNUMBERED screen, where there is
   * no question position to compute one from. The guardian beat is the only
   * screen that uses it; the ratified simulation shows it at 40 percent.
   */
  fillOverride?: number;
};

/**
 * Where a screen sits, given the flow this athlete is walking.
 *
 * Returns null for a screen that is not in the flow at all (a doorstep screen,
 * or one this athlete never sees) — the shell reads null as "no segments",
 * which is exactly the doorstep rule.
 */
export function chapterProgressFor(
  flow: readonly ChapterScreenId[],
  screen: ChapterScreenId,
): ChapterProgress | null {
  const chapter = CHAPTER_OF[screen];
  if (chapter === 0) return null;
  if (!flow.includes(screen)) return null;

  const questions = chapterQuestions(flow, chapter);
  if (!isChapterQuestion(screen)) {
    return { n: chapter, questionIndex: null, questionCount: questions.length };
  }
  return {
    n: chapter,
    questionIndex: questions.indexOf(screen) + 1,
    questionCount: questions.length,
  };
}

/**
 * Fill for one segment, 0 to 100.
 *
 * Done chapters are full, later chapters empty, and the current chapter fills
 * proportionally to the questions already behind the athlete — `(N - 1) / M`,
 * so chapter entry reads as a fresh segment rather than as progress not yet
 * made. Mirrors `Math.round(100 * list.indexOf(cur) / list.length)` in the
 * ratified simulation.
 */
export function segmentFillPercent(
  segment: ChapterNumber,
  progress: ChapterProgress,
): number {
  if (segment < progress.n) return 100;
  if (segment > progress.n) return 0;
  if (progress.questionIndex == null) {
    return Math.round(100 * Math.min(1, Math.max(0, progress.fillOverride ?? 0)));
  }
  if (progress.questionCount <= 0) return 0;
  return Math.round((100 * (progress.questionIndex - 1)) / progress.questionCount);
}

// ──────────────────────────────────────────────────────────────────────────
// The strings the chrome prints
// ──────────────────────────────────────────────────────────────────────────

/** The ambient-panel kicker. Replaces "Step N of 6". */
export function chapterKicker(n: ChapterNumber): string {
  return `Chapter ${n} of ${CHAPTER_TOTAL}`;
}

/**
 * The quiet line under the segments. Never a whole-flow count: the biggest
 * number anyone sees is this chapter's own length.
 */
export function chapterCountLine(progress: ChapterProgress): string | null {
  if (progress.questionIndex == null || progress.questionCount <= 0) return null;
  return `Question ${progress.questionIndex} of ${progress.questionCount} in this chapter`;
}

/**
 * The screen-reader sentence. Replaces "Step N of 7 · {label}".
 * Ratified wording: "Chapter 3 of 4, Finding your pace, question 2 of 4".
 */
export function chapterAriaLive(progress: ChapterProgress): string {
  const chapter = chapterByNumber(progress.n);
  const head = `Chapter ${progress.n} of ${CHAPTER_TOTAL}, ${chapter.name}`;
  if (progress.questionIndex == null || progress.questionCount <= 0) return head;
  return `${head}, question ${progress.questionIndex} of ${progress.questionCount}`;
}

// ──────────────────────────────────────────────────────────────────────────
// Part 7 (flag `onboarding_2_0_v1`): the tints as tokens
// ──────────────────────────────────────────────────────────────────────────

/**
 * The four chapter tints, now tokens in `styles/tokens.css` (block "Part 7":
 * `--chapter-N-tint`, and `--doorstep-tint` for the doorstep). The literals above
 * stay as they are: they are what the flag-off shell and the existing pins read.
 *
 * Only the restyled shell calls this. A colour that is not one of the four (the
 * guardian beat's `var(--signal-soft)`) passes through unchanged.
 */
export function onboardingTintVar(ambientBg: string): string {
  const hit = ONBOARDING_CHAPTERS.find((c) => c.ambientBg.toLowerCase() === ambientBg.toLowerCase());
  return hit ? `var(--chapter-${hit.n}-tint)` : ambientBg;
}
